import React, { useState, useEffect } from 'react';
import AvgEscReportFilter from './AvgEscReportFilter';
import SpinnerController from '../spinnerController/Spinner';
import Pagination from './Pagination';

const getNameFromEmail = (email) => {
  if (!email) return '';
  const localPart = email.split('@')[0];
  const nameParts = localPart.split('.');
  if (nameParts.length >= 2) {
    return `${nameParts[0]} ${nameParts[1]}`.replace(/\b\w/g, char => char.toUpperCase());
  }
  return localPart.replace(/\b\w/g, char => char.toUpperCase());
};

const AvgEscReportTable = ({ project, handleFilter, ticketDetails, setProject, filterError, years, setYear, setQuarter }) => {
  const [averageCases, setAverageCases] = useState({});
  const [emailRoles, setEmailRoles] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [roleSortConfig, setRoleSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState(''); // Added search query state

  useEffect(() => {
    if (ticketDetails.tickets) {
      const avgCases = {};
      const roles = {};
      ticketDetails.tickets.forEach(ticket => {
        if (ticket.email) { 
          if (!avgCases[ticket.email]) {
            avgCases[ticket.email] = 1;
          } else {
            avgCases[ticket.email]++;
          }
          roles[ticket.email] = ticket.role || ''; 
        }
      });
      setAverageCases(avgCases);
      setEmailRoles(roles);
    }
  }, [ticketDetails]);

  const sortedEmails = Object.keys(emailRoles).sort((a, b) => {
    if (sortConfig.key === 'averageCases' && averageCases[a] !== averageCases[b]) {
      if (sortConfig.direction === 'asc') {
        return averageCases[a] - averageCases[b];
      } else {
        return averageCases[b] - averageCases[a];
      }
    } else if (roleSortConfig.key === 'role' && emailRoles[a] !== emailRoles[b]) {
      if (roleSortConfig.direction === 'asc') {
        return emailRoles[a].localeCompare(emailRoles[b]);
      } else {
        return emailRoles[b].localeCompare(emailRoles[a]);
      }
    }
    return 0;
  });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setRoleSortConfig({ key: null, direction: 'asc' }); // Reset role sorting
  };

  const requestRoleSort = (key) => {
    let direction = 'asc';
    if (roleSortConfig.key === key && roleSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setRoleSortConfig({ key, direction });
    setSortConfig({ key: null, direction: 'asc' }); 
  };

 
  const getSortArrow = (config, key) => {
    if (config.key === key) {
      return config.direction === 'asc' ? '▲' : '▼';
    }
    return '▲'; 
  };

  
  const filteredEmails = sortedEmails.filter(email => {
    const name = getNameFromEmail(email).toLowerCase();
    const emailLowerCase = email.toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || emailLowerCase.includes(query); 
  });

  return (
    <div className='max-w-6xl flex flex-col px-10 justify-center container mx-auto pt-10' style={{ maxWidth: '1400px' }}>
      <div className='w-full text-center mb-4'>
        <h1 className='text-2xl font-bold text-gray-700'>Average Escalation Report</h1>
      </div>

   
    

      <div className="table-wrp block max-h-[41rem] overflow-x-auto relative shadow-md sm:rounded-lg bg-gray-500 w-full">
        <table className="w-full text-sm text-center text-gray-500">
          <caption className="sticky top-0 p-5 text-lg font-semibold text-left text-gray-700 bg-white border-b-2 border-gray-500">
          
            <div className="flex items-center justify-between">
          
              <AvgEscReportFilter project={project} handleFilter={handleFilter} setProject={setProject} setYear={setYear} years={years} setQuarter={setQuarter} />
              <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <svg
                        aria-hidden="true"
                        className="w-5 h-5 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </div>
                    <input
                      type="text"
                      className="w-64 p-0.5 border border-gray-300 rounded text-center pl-10 "
                      placeholder="Search by name or email"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
              {filterError && (
                <h6 className="pt-2 text-xs font-sm text-red-700">* {filterError}</h6>
              )}
              
            </div>
            
          </caption>
         
          <thead className="sticky top-[88px] text-xs text-gray-900 uppercase bg-white border-b-2 border-gray-500">
            <tr>
              <th scope="col" className="py-3 px-6">Escalated By</th>
              <th scope="col" className="py-3 px-6">Email</th>
              <th scope="col" className="py-3 px-6" onClick={() => requestRoleSort('role')} style={{ cursor: 'pointer' }}>
                Role {getSortArrow(roleSortConfig, 'role')}
              </th>
              <th scope="col" className="py-3 px-6" onClick={() => requestSort('averageCases')} style={{ cursor: 'pointer' }}>
                Average Case {getSortArrow(sortConfig, 'averageCases')}
              </th>
            </tr>
          </thead>
          <tbody>
            {!ticketDetails.tickets
              ? <tr key="loading">
                  <td colSpan="5" className="bg-gray-50 p-4">
                    <div className="flex justify-center">
                      <SpinnerController />
                    </div>
                  </td>
                </tr>
              : !filteredEmails.length
                ? <tr key="nodata">
                    <td className="bg-gray-50 p-4" colSpan="5">No data available</td>
                  </tr>
                : <>
                    {filteredEmails.map((email, i) => (
                      <tr key={i} className="bg-white text-gray-900 border-b hover:bg-myellow">
                        <td className="py-4 px-6 bg-myellow font-medium font-semibold text-gray-900">
                          {getNameFromEmail(email)}
                        </td>
                        <td className="py-4 px-6 font-medium font-semibold text-gray-900">
                          {email}
                        </td>
                        <td className="py-3 px-2 min-w-[100px]">
                          {emailRoles[email] || ''}
                        </td>
                        <td className="py-4 px-6">
                          {averageCases[email] || 0}
                        </td>
                      </tr>
                    ))}
                  </>
            }
          </tbody>
        </table>
      </div>
      <Pagination project={project} pageNo={ticketDetails?.tickets?.length ? ticketDetails?.tickets?.length : 0} length={ticketDetails.tLength} />
    </div>
  );
}

export default AvgEscReportTable;
